import {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  JSX,
  KeyboardEventHandler,
  Ref,
  createRef,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {styled} from 'styled-components';

import {withThemeOverride} from '@shared_web/components/core/_base';
import {InputLabel, borderAndBackground} from '@shared_web/components/core/input';
import {cssPx, optional, optionalPx, optionalRaw} from '@shared_web/lib/styled_utils';
import {useIsMounted} from '@shared_web/lib/use_is_mounted';
import {FrontendTheme} from '@shared_web/theme/theme_model';

type TextareaResize = 'vertical' | 'horizontal' | 'both' | 'none';

interface TextareaBaseProps<T> {
  ref?: Ref<HTMLTextAreaElement>;
  value: T;
  syncState?: (val: T) => void;
  asString?: (value: T) => string;
  fromString?: (value: string) => T;
  label?: string | JSX.Element;
  focusOnMount?: boolean;
  width?: number | string;
  height?: number | string;
  resize?: TextareaResize; // default to 'vertical'
  rowLabel?: boolean;
}

type TextareaProps<T> = TextareaBaseProps<T> &
  Omit<ComponentPropsWithoutRef<'textarea'>, 'style' | 'value' | 'children'>;

// eslint-disable-next-line react/function-component-definition
function TextareaInternal<T>(props: TextareaProps<T>, ref: Ref<HTMLTextAreaElement>): JSX.Element {
  const {
    syncState,
    value,
    asString,
    fromString,
    label,
    focusOnMount,
    width,
    height,
    resize = 'vertical',
    rowLabel,
    ...textareaProps
  } = props;
  const internalRef = createRef<HTMLTextAreaElement>();
  const stringify = useCallback(
    (val: T) => (asString === undefined ? (val === undefined ? '' : String(val)) : asString(val)),
    [asString]
  );

  const [internalStr, setInternalStr] = useState(stringify(value));
  const isMounted = useIsMounted();
  const handleTextareaChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    evt => {
      if (!(syncState && isMounted.current)) {
        return;
      }
      setInternalStr(evt.currentTarget.value);
      try {
        const val =
          fromString === undefined
            ? (evt.currentTarget.value as unknown as T)
            : fromString(evt.currentTarget.value);
        syncState(val);
      } catch {
        // invalid textarea, don't call syncState
      }
    },
    [fromString, isMounted, syncState]
  );

  // Stop propagation when pressing "Enter" to prevent auto submitting forms
  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(evt => {
    if (evt.key === 'Enter') {
      evt.stopPropagation();
    }
  }, []);

  useEffect(() => setInternalStr(stringify(value)), [stringify, value]);

  useEffect(() => {
    const actualRef = ref ?? internalRef;
    if (focusOnMount && typeof actualRef === 'object') {
      actualRef.current?.focus();
    }
  }, [focusOnMount, internalRef, ref]);

  const textarea = (
    <StyledTextarea
      ref={ref ?? internalRef}
      spellCheck={false}
      onChange={handleTextareaChange}
      onKeyDown={handleKeyDown}
      value={internalStr}
      $width={width}
      $height={height}
      $resize={resize}
      {...textareaProps}
    />
  );

  if (label !== undefined) {
    const el = (
      <InputLabel value={label} $labelPosition={'left'}>
        {textarea}
      </InputLabel>
    );
    // Wrap in a <div> (flex column) so that the label is on top of the input (unless `rowLabel` is enabled)
    return rowLabel ? el : <ColumnLabel>{el}</ColumnLabel>;
  }

  return textarea;
}

const BaseTextarea = withThemeOverride(forwardRef(TextareaInternal));
BaseTextarea.displayName = 'Textarea';

export const Textarea = BaseTextarea as <Item>(
  props: TextareaProps<Item> & {
    themeOverrides?: FrontendTheme;
  }
) => JSX.Element;

export const StyledTextarea = styled.textarea<{
  $width?: number | string;
  $height?: number | string;
  $resize: TextareaResize;
}>`
  ${p => optionalPx('width', p.$width)};
  ${p => optionalPx('height', p.$height)};

  resize: ${p => p.$resize};
  outline: none;
  ${p => optionalPx('padding-top', p.theme.textarea?.paddingTop)}
  ${p => optionalPx('padding-right', p.theme.textarea?.paddingRight)}
      ${p => optionalPx('padding-bottom', p.theme.textarea?.paddingBottom)}
      ${p => optionalPx('padding-left', p.theme.textarea?.paddingLeft)}
      
      ${p => optional('font-family', p.theme.textarea?.fontFamily)}
      ${p => optional('font-weight', p.theme.textarea?.fontWeight)}
      ${p => optionalPx('font-size', p.theme.textarea?.fontSize)}
      ${p => optional('color', p.theme.textarea?.textColor)}
      
      ${p => optionalPx('border-radius', p.theme.textarea?.borderRadius)}
      ${p =>
        borderAndBackground({
          borderColor: p.theme.textarea?.borderColor,
          backgroundColor: p.theme.textarea?.backgroundColorActive,
          borderWidth: p.theme.textarea?.borderWidth,
        })}
    
      &:hover {
    ${p =>
      borderAndBackground({
        borderColor: p.theme.textarea?.hoverBorderColor,
        backgroundColor: p.theme.textarea?.backgroundColorHover,
        borderWidth: p.theme.textarea?.borderWidth,
      })}
  }

  &:active:not([disabled]),
  &:focus:not([disabled]) {
    ${p =>
      borderAndBackground({
        borderColor: p.theme.textarea?.focusBorderColor,
        backgroundColor: p.theme.textarea?.backgroundColorFocus,
        borderWidth: p.theme.textarea?.focusBorderWidth,
      })}
    ${p =>
      optionalRaw(
        p.theme.textarea?.focusOutlineWidth,
        v => `box-shadow: 0 0 0 ${cssPx(v)} ${p.theme.textarea?.focusOutlineColor};`
      )}
        ${p => optional('color', p.theme.textarea?.focusTextColor)}
  }

  &:disabled {
    ${p => optional('color', p.theme.textarea?.textColorDisabled)}
    box-shadow: none;
    ${p =>
      borderAndBackground({
        borderColor: p.theme.textarea?.borderColor,
        backgroundColor: p.theme.textarea?.backgroundColorDisabled,
        borderWidth: p.theme.textarea?.borderWidth,
      })}
  }
`;

const ColumnLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
